import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledPasswordField } from '@/common/components/Fields/ControlledPasswordField/ControlledPasswordField';
import { Form } from '@/common/components/Form/Form';
import { Icon } from '@/common/components/Icons/Icon';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { ROUTES } from '@/common/constants/routes';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useAuthenticationContext } from '@/common/contexts/authentication/AuthenticationContext';
import { BiometricsType, useBiometrics } from '@/common/hooks/useBiometrics';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { SecureStorageService } from '@/common/integrations/secure-storage/secure-storage.service';
import { StorageService } from '@/common/integrations/storage/storage.service';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import errorUtils from '@/common/utils/error';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'expo-router';
import { ResetPasswordModal } from '../components/ResetPasswordModal/ResetPasswordModal';
import { ISoftLoginFormData, formSchema } from './SoftLoginPage.schema';
import { styles } from './SoftLoginPage.styles';

const SoftLoginPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    biometricsEnabled,
    biometricsAvailable,
    authenticateWithBiometrics,
    biometricsType,
  } = useBiometrics();
  const { setNeedsLogin, signIn, signOut } = useAuth();
  const { setContextCredentials } = useAuthenticationContext();
  const breakpoints = useBreakpoints(['lg']);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [loggedInUserShortName, setLoggedInUserShortName] = useState<
    string | null
  >(null);

  const shouldUseBiometrics = biometricsEnabled && biometricsAvailable;

  useEffect(() => {
    const fetchLoggedInUserShortName = async () => {
      const shortName = await StorageService.getLoggedInUserShortName();
      setLoggedInUserShortName(shortName ?? null);
    };
    fetchLoggedInUserShortName();
  }, []);

  const onSignIn = useCallback(
    async (email: string, password: string) => {
      try {
        const signInResponse = await AuthService.signIn({
          email,
          password,
        });

        setContextCredentials({
          email,
          password,
          authState: signInResponse,
        });

        if ('reason' in signInResponse) {
          if (signInResponse.reason === 'OTP_REQUIRED') {
            AnalyticsService.trackCustomEvent('soft_login_otp_required');
            notify({
              type: 'success',
              message: t('authentication.signin.form.mfa.otp.title', {
                suffix: signInResponse.phoneLastDigits,
              }),
            });
            router.navigate(ROUTES.OTP);
          }
          return;
        }

        if ('user' in signInResponse) {
          setNeedsLogin(false);
          signIn(signInResponse);

          const { user } = signInResponse;

          AnalyticsService.trackCustomEvent('soft_login_success', {
            employee_id: user.id,
            employee_email: user.email,
            company_id: user.companyId,
          });
        }
      } catch (error: any) {
        const errorMessage = errorUtils.getErrorMessageKey(error?.errorCode);
        notify({
          type: 'error',
          message: t(errorMessage),
        });
        AnalyticsService.trackCustomEvent('soft_login_failed');
      }
    },
    [router, setContextCredentials, setNeedsLogin, signIn, t]
  );

  const biometricsAuthenticate = useCallback(async () => {
    const response = await authenticateWithBiometrics();
    if (response?.email && response.password) {
      onSignIn(response.email, response.password);
    }
  }, [authenticateWithBiometrics, onSignIn]);

  useEffect(() => {
    if (shouldUseBiometrics) biometricsAuthenticate();
  }, [shouldUseBiometrics, biometricsAuthenticate]);

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      password: '',
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async ({ password }: ISoftLoginFormData) => {
    try {
      const email =
        await SecureStorageService.getDataFromSecureStorage('email');

      if (email && password) {
        await onSignIn(email, password);
      }
    } catch (error: any) {
      const errorMessage = errorUtils.getErrorMessageKey(error?.errorCode);
      notify({
        type: 'error',
        message: t(errorMessage),
      });
      AnalyticsService.trackCustomEvent('soft_login_failed');
    }
  };

  const handleChangeUser = async () => {
    await AuthService.signOut();

    signOut();
    router.push(ROUTES.SIGNIN);
  };

  const actions = (inLayout: boolean) => (
    <Stack
      horizontal
      gap="lg"
      align="center"
      wrap
      className={inLayout ? styles.actions : ''}
    >
      <TextAction size="sm" onPress={() => setResetPasswordVisible(true)}>
        {t('authentication.signin.forgot_password')}
      </TextAction>
      <TextAction size="sm" onPress={handleChangeUser}>
        {t('authentication.signin.change_user')}
      </TextAction>
    </Stack>
  );

  return (
    <AuthFormLayout
      title={
        loggedInUserShortName
          ? t('authentication.soft_login_title.name', {
              name: loggedInUserShortName,
            })
          : t('authentication.soft_login_title')
      }
      footer={
        <>
          <Button
            type="submit"
            onPress={methods.handleSubmit(onSubmit)}
            loading={methods.formState.isSubmitting}
          >
            {t('authentication.signin.form.button')}
          </Button>
          {actions(true)}
        </>
      }
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="xl">
            <PageHeader
              title={t('authentication.signin.form.button')}
              actions={
                shouldUseBiometrics ? (
                  <Button
                    onPress={biometricsAuthenticate}
                    variant="tertiary"
                    icon={
                      <Icon
                        icon={
                          biometricsType === BiometricsType.FACE_ID
                            ? 'face-viewfinder'
                            : 'fingerprint'
                        }
                      />
                    }
                  >
                    {t(`authentication.signin.form.${biometricsType}.button`)}
                  </Button>
                ) : undefined
              }
            />
            <Stack gap="xs" align="end">
              <ControlledPasswordField
                name="password"
                label={t('authentication.signin.form.password.label')}
              />
            </Stack>
            {!breakpoints.lg && actions(false)}
          </Stack>
        </Form>
      </FormProvider>
      {resetPasswordVisible && (
        <ResetPasswordModal onClose={() => setResetPasswordVisible(false)} />
      )}
    </AuthFormLayout>
  );
};

export default SoftLoginPage;
